import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { fmtMXN } from "@/lib/format";
import { Star } from "lucide-react";

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/products").then((r) => {
      setProducts(r.data);
      setLoading(false);
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = async (id, patch) => {
    try {
      const { data } = await api.patch(`/admin/products/${id}`, patch);
      setProducts((ps) => ps.map((p) => (p.id === id ? (data || { ...p, ...patch }) : p)));
    } catch (e) {
      alert(e.response?.data?.detail || "Error al actualizar");
    }
  };

  const updateVariantPrice = async (product, variantId, newPrice) => {
    const variants = (product.variants || []).map((v) =>
      v.id === variantId ? { ...v, price: parseFloat(newPrice) } : v
    );
    // Update "price" field too if editing the last-added / default variant (first)
    const primary = variants[0];
    await update(product.id, { variants, price: primary?.price ?? product.price });
  };

  const filtered = products.filter((p) => !filter || p.category === filter);
  const categories = [...new Set(products.map((p) => p.category))];

  return (
    <div className="p-8 md:p-12" data-testid="admin-products">
      <div className="mb-8">
        <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-2">⟡ Admin</div>
        <h1 className="font-display text-4xl md:text-5xl tracking-wider uppercase text-white leading-none">Productos</h1>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <button
          onClick={() => setFilter("")}
          className={`px-4 py-2 font-display text-sm tracking-widest uppercase border transition ${
            !filter ? "bg-arre-red border-arre-red text-white" : "border-arre-border text-white/60 hover:text-white"
          }`}
        >
          Todos ({products.length})
        </button>
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setFilter(c)}
            className={`px-4 py-2 font-display text-sm tracking-widest uppercase border transition ${
              filter === c ? "bg-arre-red border-arre-red text-white" : "border-arre-border text-white/60 hover:text-white"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="bg-arre-surface border border-arre-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-arre-border text-[10px] tracking-[0.2em] uppercase text-white/50">
              <th className="text-left p-4">Producto</th>
              <th className="text-left p-4">Categoría</th>
              <th className="text-left p-4">Precios (editables)</th>
              <th className="text-left p-4">Stock</th>
              <th className="text-left p-4">Destacado</th>
              <th className="text-left p-4">Activo</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="6" className="p-8 text-center text-white/50">Cargando...</td></tr>}
            {filtered.map((p) => (
              <tr key={p.id} className="border-b border-arre-border/50" data-testid={`admin-product-${p.slug}`}>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <img src={p.image} alt={p.name} className="w-10 h-10 object-cover border border-arre-border" />
                    <div>
                      <div className="font-display text-white">{p.name}</div>
                      <div className="text-xs text-white/40">{p.sku}</div>
                    </div>
                  </div>
                </td>
                <td className="p-4 text-white/70 capitalize">{p.category}</td>
                <td className="p-4">
                  <div className="space-y-1.5">
                    {(p.variants || [{ id: "default", label: p.unit || "Precio", price: p.price }]).map((v) => (
                      <div key={v.id} className="flex items-center gap-2">
                        <span className="text-[10px] tracking-widest uppercase text-white/50 w-16 text-right">
                          {v.id === "350g" ? "350 g" : v.id === "1kg" ? "1 kg" : v.label?.split(" ")[0] || v.id}
                        </span>
                        <span className="text-white/60 text-sm">$</span>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          defaultValue={v.price}
                          onBlur={(e) => {
                            const nv = parseFloat(e.target.value);
                            if (!isNaN(nv) && nv > 0 && nv !== v.price) {
                              if (p.variants?.length) {
                                updateVariantPrice(p, v.id, nv);
                              } else {
                                update(p.id, { price: nv });
                              }
                            }
                          }}
                          className="w-24 bg-arre-black border border-arre-border px-2 py-1 text-white text-sm focus:outline-none focus:border-arre-red"
                          data-testid={`price-${p.slug}-${v.id}`}
                        />
                      </div>
                    ))}
                  </div>
                </td>
                <td className="p-4">
                  <input
                    type="number"
                    min="0"
                    defaultValue={p.stock ?? 0}
                    onBlur={(e) => {
                      const v = parseInt(e.target.value, 10);
                      if (!isNaN(v) && v !== p.stock) update(p.id, { stock: v });
                    }}
                    className="w-20 bg-arre-black border border-arre-border px-2 py-1 text-white focus:outline-none focus:border-arre-red"
                    data-testid={`stock-${p.slug}`}
                  />
                </td>
                <td className="p-4">
                  <button
                    onClick={() => update(p.id, { featured: !p.featured })}
                    className={`${p.featured ? "text-arre-red" : "text-white/30"} hover:scale-110 transition`}
                    data-testid={`featured-${p.slug}`}
                    title={p.featured ? "Destacado" : "No destacado"}
                  >
                    <Star className="w-5 h-5" fill={p.featured ? "currentColor" : "none"} />
                  </button>
                </td>
                <td className="p-4">
                  <Toggle active={p.active !== false} onChange={(v) => update(p.id, { active: v })} testid={`active-${p.slug}`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function Toggle({ active, onChange, testid }) {
  return (
    <button
      onClick={() => onChange(!active)}
      className={`relative w-10 h-5 rounded-full transition ${active ? "bg-arre-red" : "bg-arre-border"}`}
      data-testid={testid}
    >
      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition ${active ? "left-[22px]" : "left-0.5"}`} />
    </button>
  );
}
